import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { placeholderFor, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import ListingThumb from './ListingThumb';

/**
 * Offer-flow photo gate, the member-facing half (plan §4, 2026-10-18).
 *
 * Reads `offer.photoGate`, which only functions/photoGate.js writes. For each
 * gated listing it shows one of two views:
 *   - the owner: "add at least two photos" with an Add photos button that
 *     deep-links to their Pedalboard edit form on the photo step and returns
 *     here afterwards;
 *   - the other party: waiting / photos added, a View listing link, any Vision
 *     flag (hostnames only) and an Approve button that writes
 *     `photoApprovals.<listingId>`; the server then recomputes the gate.
 * Decline and counter are never blocked by this panel; only Accept is, and
 * firestore.rules enforces that regardless of what the client renders.
 */

/**
 * True when the offer cannot be accepted yet. Matches firestore.rules exactly:
 * a waiting offer with NO gate yet (the server trigger has not run, or that
 * run was lost) is closed, not open. The panel shows "Checking photos" for
 * that state and nudges the server to recompute.
 */
export function isPhotoGateClosed(offer) {
  if (!offer) return false;
  if (!['pending', 'countered'].includes(offer.status)) return false;
  const gate = offer.photoGate;
  if (!gate || typeof gate !== 'object') return true;
  return gate.open !== true;
}

const RECHECK_AFTER_MS = 15000;

function useGatedListings(ids) {
  const [listings, setListings] = useState({});
  useEffect(() => {
    if (!ids.length) { setListings({}); return undefined; }
    const unsubs = ids.map((id) => onSnapshot(doc(db, 'listings', id), (snap) => {
      setListings((prev) => ({ ...prev, [id]: snap.exists() ? { id: snap.id, ...snap.data() } : null }));
    }, () => {}));
    return () => unsubs.forEach((u) => u());
  }, [ids.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps
  return listings;
}

const PANEL = { border: '1px solid #E5D9B6', background: '#FBF7EA', borderRadius: '12px', padding: '14px 16px', marginBottom: '14px', fontFamily: 'Nunito, sans-serif', textAlign: 'left' };
const ROW = { display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '10px 0', borderTop: '1px solid #EFE6CF' };
// Side by side on a phone (Jason, 2026-10-19): equal halves, no wrapping labels.
// Each button sizes to its label; the row wraps only if both cannot fit.
const ACTION_BTN = { flex: '0 1 auto', whiteSpace: 'nowrap', padding: '8px 14px', textAlign: 'center', overflow: 'visible' };
const BTN = { border: 'none', borderRadius: '25px', padding: '8px 16px', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' };

/**
 * Short host labels that link to the page the photo was found on. Falls back
 * to the bare host when the check pre-dates page capture.
 */
function hostLabel(check) {
  const pages = Array.isArray(check && check.matchedPages) ? check.matchedPages : [];
  const items = pages.length > 0
    ? pages
    : (Array.isArray(check && check.matchedHosts) ? check.matchedHosts : []).map((h) => ({ host: h, url: `https://${h}` }));
  if (items.length === 0) return null;
  return items.slice(0, 3).map((p, i) => (
    <React.Fragment key={p.url || p.host}>
      {i > 0 ? ', ' : ''}
      <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ color: '#5A9ECB' }}>{p.host}</a>
    </React.Fragment>
  ));
}

export default function PhotoGatePanel({ offer, collection, offerId, currentUserId, returnTo, onCancelOffer = null, cancelling = false }) {
  const gate = offer && offer.photoGate;
  const pending = (gate && gate.pending) || {};
  const ids = Object.keys(pending);
  const listings = useGatedListings(ids);
  const [busyId, setBusyId] = useState(null);
  const [approveError, setApproveError] = useState(null);
  const [waitedLong, setWaitedLong] = useState(false);
  const nudgedRef = useRef(false);

  // Self-heal (review, 2026-10-19): if the gate is missing on a waiting offer,
  // or says a pedal has no photos while the live listing clearly does, a lost
  // trigger run is the likely cause. After a short wait, write a one-shot
  // nudge the update trigger recomputes on. The rules let either party write it.
  const waiting = !!offer && ['pending', 'countered'].includes(offer.status);
  const gateMissing = waiting && !(gate && typeof gate === 'object');
  const liveHasPhotos = (id) => {
    const l = listings[id];
    const imgs = l && l.images && typeof l.images === 'object' ? Object.values(l.images) : [];
    return imgs.filter((u) => typeof u === 'string' && u.trim()).length >= 2;
  };
  const gateStale = waiting && !gateMissing && ids.some((id) => !(pending[id] && pending[id].hasPhotos) && liveHasPhotos(id));
  const needsNudge = gateMissing || gateStale;
  useEffect(() => {
    if (!needsNudge || !offerId || !collection || nudgedRef.current) { setWaitedLong(false); return undefined; }
    const t = setTimeout(async () => {
      setWaitedLong(true);
      nudgedRef.current = true;
      try {
        await updateDoc(doc(db, collection, offerId), { photoGateRecheckAt: serverTimestamp() });
      } catch (err) {
        console.error('Photo gate recheck nudge failed:', err);
      }
    }, RECHECK_AFTER_MS);
    return () => clearTimeout(t);
  }, [needsNudge, offerId, collection]);

  if (!currentUserId) return null;

  if (gateMissing) {
    return (
      <div style={PANEL} className="photo-gate-panel photo-gate-checking">
        <div style={{ fontWeight: 800, color: '#374151', marginBottom: '2px' }}>Checking photos…</div>
        <div style={{ fontSize: '0.85rem', color: '#6B7280', lineHeight: 1.45 }}>
          {waitedLong ? 'Taking longer than usual. This clears on its own; you can still decline or counter.' : 'One moment while the pedals in this offer are checked for photos.'}
        </div>
      </div>
    );
  }

  if (ids.length === 0) return null;

  // Gate open: keep a quiet confirmation in place of the panel rather than
  // letting the box vanish mid-interaction (Jason, 2026-10-19).
  if (gate.open === true) {
    return (
      <div style={{ ...PANEL, border: '1px solid #BBE5C8', background: '#F0FAF3' }} className="photo-gate-panel photo-gate-open">
        <div style={{ fontWeight: 800, color: '#166534' }}>Photos approved</div>
        <div style={{ fontSize: '0.85rem', color: '#166534', lineHeight: 1.45 }}>This offer can now be accepted.</div>
      </div>
    );
  }

  // Heading follows the state: photos still missing on some pedal, or every
  // pedal has photos and only the other trader's review is outstanding.
  const anyMissing = ids.some((id) => !(pending[id] && pending[id].hasPhotos));
  const heading = anyMissing
    ? 'Photos needed before this offer can be accepted'
    : 'Photo review needed before this offer can be accepted';

  const approve = async (listingId) => {
    setBusyId(listingId);
    setApproveError(null);
    try {
      // Keyed by the approver: firestore.rules lets a member write only their
      // own branch of photoApprovals, so an owner cannot forge this.
      await updateDoc(doc(db, collection, offerId), {
        [`photoApprovals.${currentUserId}.${listingId}`]: { at: serverTimestamp() },
      });
    } catch (err) {
      console.error('Photo approval failed:', err);
      setApproveError('Could not save your approval. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  // The Vision check is best effort: it only runs on some photo edits and can
  // fail quietly, so it must never hold the review hostage. Approve is held
  // back only briefly after the photos landed.
  const gateUpdatedMs = gate && gate.updatedAt && typeof gate.updatedAt.toMillis === 'function' ? gate.updatedAt.toMillis() : 0;
  const checkWindowOpen = gateUpdatedMs > 0 && Date.now() - gateUpdatedMs < 30000;

  return (
    <div style={PANEL} className="photo-gate-panel">
      <div style={{ fontWeight: 800, color: '#374151', marginBottom: '2px' }}>{heading}</div>
      <div style={{ fontSize: '0.85rem', color: '#6B7280', lineHeight: 1.45 }}>
        Either of you can still decline or counter in the meantime.
      </div>
      {ids.map((id) => {
        const entry = pending[id] || {};
        const listing = listings[id];
        const isOwner = entry.ownerId === currentUserId;
        const name = listing ? `${listing.make || ''} ${listing.model || ''}`.trim() : 'This pedal';
        const thumb = listing && listing.images && listing.images.top ? listing.images.top : placeholderFor(listing || {}).url;
        const check = listing && listing.imageCheck;
        const checked = !!(check && check.checkedAt);
        const flagged = check && check.status === 'flagged';
        const canApprove = checked || !checkWindowOpen;
        // Your own approval shows the moment it is written (the offer doc updates
        // live); the server confirms it in `photoGate` a few seconds later.
        const mine = offer.photoApprovals && offer.photoApprovals[currentUserId];
        const myApproval = !!(mine && typeof mine === 'object' && mine[id]);
        const approved = !!entry.approvedBy || (!isOwner && !!myApproval);
        const confirming = !isOwner && myApproval && !entry.approvedBy;
        if (entry.deleted || (listing === null && entry.hasPhotos === false)) {
          return (
            <div key={id} style={ROW}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: '#1F2937' }}>{name}</div>
                <div style={{ fontSize: '0.85rem', color: '#4B5563', marginTop: '2px' }}>
                  This listing was removed, so the offer can no longer be accepted.
                </div>
                {onCancelOffer && (
                  <button type="button" onClick={onCancelOffer} disabled={cancelling} style={{ ...BTN, ...ACTION_BTN, marginTop: '8px', background: 'white', color: '#4B5563', border: '1px solid #9CA3AF' }}>
                    {cancelling ? 'Cancelling…' : 'Cancel offer'}
                  </button>
                )}
              </div>
            </div>
          );
        }
        return (
          <div key={id} style={ROW}>
            <ListingThumb src={thumb} fallbackSrc={SKETCH_PLACEHOLDER_URL} alt={name} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0, background: '#F5F1E8' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: '#1F2937' }}>{name}</div>

              {isOwner && !entry.hasPhotos && (
                <>
                  <div style={{ fontSize: '0.85rem', color: '#4B5563', margin: '2px 0 8px' }}>
                    Add at least two photos of the actual pedal.
                  </div>
                  <Link
                    to={`/pedalboard?edit=${encodeURIComponent(id)}&step=photos${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`}
                    style={{ ...BTN, display: 'inline-block', background: '#C89F3C', color: 'white', textDecoration: 'none' }}
                  >
                    Add photos
                  </Link>
                </>
              )}
              {isOwner && entry.hasPhotos && !approved && (
                <div style={{ fontSize: '0.85rem', color: '#4B5563', marginTop: '2px' }}>
                  Photos added. Waiting for the other trader to review them.
                  {flagged && (
                    <div style={{ marginTop: '6px', color: '#92400E' }}>
                      One of your photos was found on {hostLabel(check)}. The other trader will see this. Replace it, or explain in a comment.
                    </div>
                  )}
                </div>
              )}
              {!isOwner && !entry.hasPhotos && (
                <div style={{ fontSize: '0.85rem', color: '#4B5563', marginTop: '2px' }}>
                  Waiting for the owner to add photos of the actual pedal.
                </div>
              )}
              {!isOwner && entry.hasPhotos && (
                <div style={{ fontSize: '0.85rem', color: '#4B5563', marginTop: '2px' }}>
                  Photos added.{' '}
                  <Link to={`/listing/${id}?fromOffer=true`} style={{ color: '#5A9ECB' }}>View listing</Link>
                  {flagged && (
                    <div style={{ marginTop: '6px', color: '#92400E', fontWeight: 700 }}>
                      We found these listing photos on {hostLabel(check)}. They may not be the owner's own photos. For your protection, review them carefully before you approve.
                    </div>
                  )}
                  {!checked && !approved && (
                    <div style={{ marginTop: '6px', color: '#6B7280' }}>{checkWindowOpen ? 'Checking photos…' : 'These photos have not been checked against other sites yet.'}</div>
                  )}
                  {approveError && (
                    <div style={{ marginTop: '6px', color: '#B91C1C', fontWeight: 700 }}>{approveError}</div>
                  )}
                  <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }} className="photo-gate-actions">
                    {approved ? (
                      <span style={{ color: '#16a34a', fontWeight: 700 }}>{confirming ? (waitedLong ? 'Approved. Still updating the offer…' : 'Approved, updating offer…') : 'Approved'}</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => approve(id)}
                        disabled={!canApprove || busyId === id}
                        style={{ ...BTN, ...ACTION_BTN, background: canApprove ? '#C89F3C' : '#D1D5DB', color: 'white', cursor: canApprove ? 'pointer' : 'not-allowed' }}
                      >
                        {busyId === id ? 'Saving…' : 'Approve photos'}
                      </button>
                    )}
                    {/* Back out from here too (Jason, 2026-10-19): the reviewer is
                        usually the party waiting, with no other way out. Stays
                        visible while the server is still confirming an approval. */}
                    {(!approved || confirming) && onCancelOffer && (
                      <button
                        type="button"
                        onClick={onCancelOffer}
                        disabled={cancelling}
                        style={{ ...BTN, ...ACTION_BTN, background: 'white', color: '#4B5563', border: '1px solid #9CA3AF', cursor: cancelling ? 'wait' : 'pointer' }}
                      >
                        {cancelling ? 'Cancelling…' : 'Cancel offer'}
                      </button>
                    )}
                  </div>
                </div>
              )}
              {isOwner && approved && (
                <div style={{ fontSize: '0.85rem', color: '#16a34a', fontWeight: 700, marginTop: '2px' }}>Photos approved</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
